import { HttpResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AlertService } from 'src/app/components/alert/alert.service';
import { IFormKey } from 'src/app/modules/shared/models/forms';
import { INavigationEvent } from 'src/app/modules/shared/models/navigation.model';
import { IProductConstraint } from 'src/app/modules/shared/models/operation.model';
import { DATE_FORMAT, EPolicyTypes, IPolicyDetail, IPolicyDetailSubject } from 'src/app/modules/shared/models/policy.model';
import { LoggerService } from 'src/app/services/logger.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { CommonUtils } from 'src/app/utils/CommonUtils';
import { PolicyService } from '../../policy.service';
import { CANNOT_OPERATE_WARN_MESSAGE, CONTRACTOR_DOC_EXPIRED_MESSAGE, CONTRACTOR_DOC_SERVICE_ERROR_MESSAGE, FULL_ADDRESS_UNI30_KEY, FULL_ADDRESS_UNI30_LABEL, IPolicyKeys, POLICY_CONSTRAINT_ERROR_MESSAGE, policy_holder_keys_PF, policy_holder_keys_PG } from './policy-detail.constants';
import moment = require('moment');

@Component({
  selector: 'app-policy-detail',
  templateUrl: './policy-detail.component.html',
  styleUrl: './policy-detail.component.scss'
})
export class PolicyDetailComponent implements OnInit, OnDestroy {

  detailKeys: IPolicyKeys = null;
  isContractorDocumentExpired: boolean = false;
  errorRetrieveingRegistryDetail: boolean = false;
  destroy$ = new Subject();

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _spinner: SpinnerService,
    private _policyService: PolicyService,
    private _alertService: AlertService
  ) { }

  ngOnInit(): void {
    this.getUrlParameters();
    if (this.contractNumber) {
      this.getDetail();
    }
    this._policyService.refreshDetail.pipe(takeUntil(this.destroy$)).subscribe(refresh => {
      if (refresh) {
        this.getDetail();
      }
    })
  }

  get policyId(): string {
    return this._policyService.policyId;
  }

  get contractNumber(): string {
    return this._policyService.contractNumber;
  }

  get policyDetail(): IPolicyDetail {
    return this._policyService.policyDetail;
  }

  get subject(): IPolicyDetailSubject {
    const subject: IPolicyDetailSubject = this._policyService.subject;
    if (subject) {
      const fullAddress = this._policyService.isDetailPrevidenziale
        ? CommonUtils.checkAndGet(this.policyDetail, 'intesaFurtherDetails.uni30.policyHolder.fullAddress', null)
        : CommonUtils.checkAndGet(this.policyDetail, 'intesaFurtherDetails.uni20.policyHolder.datiResidenza.indirizzoCompleto', null);
      subject.ndg = CommonUtils.checkAndGet(this.policyDetail, 'policyExtraInfo.ndg', null);
      subject.fullAddress = fullAddress;
    }
    return subject;
  }

  get operationId(): string {
    return this._policyService.operationId;
  }

  get ndg(): string {
    return this._policyService.ndg;
  }

  get isDetailPage(): boolean {
    const check = this._router.url.includes('operations');
    return !check;
  }

  get subjectName(): string {
    if (this.subject) {
      const name = this.subject.type === 'PF' ? `${this.subject.name} ${this.subject.surname}` : this.subject.companyName;
      return name;
    }
    return '';
  }

  get subjectKeys(): IFormKey[] {
    if (this.subject) {
      const subjectKeys = this.subject.type === 'PF' ? CommonUtils.assign(policy_holder_keys_PF) : CommonUtils.assign(policy_holder_keys_PG);

      if (this._policyService.policyType === EPolicyTypes.PREVIDENZIALI)
        subjectKeys.push({
          label: FULL_ADDRESS_UNI30_LABEL,
          key: FULL_ADDRESS_UNI30_KEY,
        });

      return subjectKeys;
    }
    return [];
  }

  get isOFSAuthorized(): boolean {
    return !CommonUtils.checkAndGet(this.policyDetail, 'isUserOperatingOFSWithoutCustomerOFS', null);
  }

  get cannotOperateWarnMessage(): string {
    return CANNOT_OPERATE_WARN_MESSAGE;
  }

  get contractorDocExpiredWarnMessage(): string {
    return CONTRACTOR_DOC_EXPIRED_MESSAGE;
  }

  get contractorDocServiceErrorMessage(): string {
    return CONTRACTOR_DOC_SERVICE_ERROR_MESSAGE;
  }

  get policyConstraintMessage(): string {
    return POLICY_CONSTRAINT_ERROR_MESSAGE;
  }

  get showPolicyConstraintMessage(): boolean {
    return this._policyService.policyHasConstraint;
  }

  getUrlParameters() {
    const url = this._router.url;
    const id = this._activatedRoute.snapshot.paramMap.get('id');
    let operationId = null;
    if (url) {
      const op = url.split('operations/')[1];
      if (op) operationId = op;
    }
    this._policyService.setRouteIds(id, operationId);
    LoggerService.logInfo(this.constructor.name, 'getUrlParameters', { id, operationId });
  }

  onNavigationRequested(event: INavigationEvent) {
    LoggerService.logInfo(this.constructor.name, 'onNavigationRequested', { event });
    this._router.navigate([event.url], { state: event.state })
      .then(success => {
        if (success) {
          this.getUrlParameters();
        }
      });
  }

  getDetail() {
    this._spinner.show();
    this._policyService.getPolicyByContractNumber()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: IPolicyDetail) => {
          LoggerService.logInfo(this.constructor.name, 'getDetail', { response });
          this._policyService.setDetail(response);
          const policyType = CommonUtils.checkAndGet(response, 'summary.policyType', null);
          this.getDetailKeysByType(policyType);
          // recupero dettaglio anagrafico contraente per check su documento
          this.getRegistryDetail(this._policyService.ndg);
          // recupero limiti operazioni
          this.getOperationsContraints();
          this._spinner.hide();
        },
        error: (error: HttpResponse<any>) => {
          LoggerService.logError(this.constructor.name, 'getDetail', { error });
          this._alertService.openErrorMessage('Errore nel recupero del dettaglio polizza', 'getPolicyById', error);
          this._spinner.hide();
        }
      })
  }

  getDetailKeysByType(type: EPolicyTypes) {
    this.detailKeys = this._policyService.getDetailKeys(type);
    LoggerService.logInfo(this.constructor.name, 'getDetailKeysByType', { keys: this.detailKeys });
  }

  getRegistryDetail(ndg: string): void {
    this._spinner.show();
    this._policyService.retrieveNdgList(this._policyService.contractNumber, ndg)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response) {
            const documentExpiryDate = response?.['ns2:dettaglioAnagrafico']['ns2:datiDocumento']['ns2:dataScadenza'];
            if (documentExpiryDate) {
              this.isContractorDocumentExpired = moment(documentExpiryDate, DATE_FORMAT).isBefore(moment(), 'day');
            }
          }
          this.errorRetrieveingRegistryDetail = false;
          this._spinner.hide();
        },
        error: error => {
          LoggerService.logError(this.constructor.name, 'getRegistryDetail', error);
          this.isContractorDocumentExpired = false;
          this.errorRetrieveingRegistryDetail = true;
          this._alertService.openErrorMessage('Errore nel recupero dei dati del contraente', 'getRegistryDetail', error);
          this._spinner.hide();
        }
      })
  }

  getOperationsContraints(): void {
    this._spinner.show();
    this._policyService.retrieveOperationsContraints()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: IProductConstraint[]) => {
          if (response) {
            this._policyService.setOperationsConstraints(response);
          }
          this._spinner.hide();
        },
        error: error => {
          LoggerService.logError(this.constructor.name, 'getOperationsContraints', error);
          this._alertService.openErrorMessage('Errore nel recupero dei limiti delle operazioni', 'getOperationsContraints', error);
          this._spinner.hide();
        }
      });
  }

  ngOnDestroy(): void {
    this._policyService.clearAll();
    this.destroy$.next(null);
    this.destroy$.complete();
  }

}
